import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Phone, MapPin } from "lucide-react";
import { buildWhatsAppLink, WHATSAPP_NUMBER, WHATSAPP_NUMBER_2, INSTAGRAM_URL, YOUTUBE_URL } from "../lib/api";
import { WhatsAppIcon, InstagramIcon, YoutubeIcon } from "./BrandIcons";

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const goAnchor = (hash) => (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/${hash}`);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#0F1245] text-[#F4F7FE]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline" style={{ color: "#B7E4F4" }}>Wholesale Only · Delhi</div>
            <div className="mt-4">
              <img
                src="/images/logo.jpg"
                alt="Style India · Manufacturer & Wholesaler"
                className="h-32 md:h-40 w-auto object-contain"
              />
            </div>
            <p className="mt-6 text-[14px] text-white/60 max-w-sm leading-relaxed">
              We make and supply ladies ethnic wear in bulk — suits, kurtas, kaftans, co-ord sets and party wear — for shops, boutiques and online sellers. Based in Lajpat Nagar, New Delhi.
            </p>

            <div className="mt-8 flex items-center gap-3">
              <a
                href={buildWhatsAppLink("Hi Style India Delhi — I came in via the website and would like to start a wholesale conversation.")}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-whatsapp-link"
                className="w-10 h-10 border border-white/20 flex items-center justify-center bg-white hover:scale-110 transition-transform"
                aria-label="WhatsApp"
              >
                <WhatsAppIcon size={18} />
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram-link"
                className="w-10 h-10 border border-white/20 flex items-center justify-center bg-white hover:scale-110 transition-transform"
                aria-label="Instagram"
              >
                <InstagramIcon size={18} />
              </a>
              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-youtube-link"
                className="w-10 h-10 border border-white/20 flex items-center justify-center bg-white hover:scale-110 transition-transform"
                aria-label="YouTube"
              >
                <YoutubeIcon size={18} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="overline" style={{ color: "#B7E4F4" }}>Navigate</div>
            <ul className="mt-5 space-y-3 text-[14px]">
              <li><a href="/" onClick={goAnchor("#top")} className="hover:text-[#B7E4F4] transition-colors">Home</a></li>
              <li><a href="/#about" onClick={goAnchor("#about")} className="hover:text-[#B7E4F4] transition-colors">About</a></li>
              <li><a href="/#collection" onClick={goAnchor("#collection")} className="hover:text-[#B7E4F4] transition-colors">Our Collection</a></li>
              <li><a href="/#showroom" onClick={goAnchor("#showroom")} className="hover:text-[#B7E4F4] transition-colors">Visit Us</a></li>
              <li><a href="/#contact" onClick={goAnchor("#contact")} className="hover:text-[#B7E4F4] transition-colors">Contact</a></li>
              <li><Link to="/admin/login" className="text-white/40 hover:text-[#B7E4F4] transition-colors text-xs tracking-widest uppercase">Admin</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="overline" style={{ color: "#B7E4F4" }}>Visit · Lajpat Nagar</div>
            <ul className="mt-5 space-y-4 text-[14px] text-white/75">
              <li className="flex gap-3">
                <MapPin size={16} strokeWidth={1.5} className="text-[#B7E4F4] mt-0.5 shrink-0" />
                <span>
                  <span className="text-white">Shop 01</span> · D-43, Old Double Storey<br />
                  Lajpat Nagar IV, New Delhi 110024
                </span>
              </li>
              <li className="flex gap-3">
                <MapPin size={16} strokeWidth={1.5} className="text-[#B7E4F4] mt-0.5 shrink-0" />
                <span>
                  <span className="text-white">Shop 02</span> · D-29, Old Double Storey<br />
                  Lajpat Nagar IV, New Delhi 110024
                </span>
              </li>
              <li className="flex gap-3">
                <Phone size={16} strokeWidth={1.5} className="text-[#B7E4F4] mt-0.5 shrink-0" />
                <div className="flex flex-col gap-0.5">
                  <a href={`tel:+${WHATSAPP_NUMBER}`} className="hover:text-[#B7E4F4] transition-colors">+91 97118 84840</a>
                  <a href={`tel:+${WHATSAPP_NUMBER_2}`} className="hover:text-[#B7E4F4] transition-colors">+91 98113 37753</a>
                </div>
              </li>
              <li className="text-[13px] text-white/55">Tue – Sun · 10:00 AM – 8:00 PM (Closed Monday)</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[12px] text-white/40">
          <span>© {new Date().getFullYear()} Style India Delhi. All rights reserved.</span>
          <span className="tracking-widest uppercase">Made in Delhi · Wholesale Only · Worldwide Shipping</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
